import { zodResolver } from '@hookform/resolvers/zod'
import { getRouteApi } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useForm, type DefaultValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Loading from '@/components/Loading'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import { daysOfWeek, type DayOfWeek } from '@/constants/days-of-week'
import { toast } from '@/hooks/useToast'
import {
  useTimetableSetttings,
  type TimetableSettings
} from '@/modules/timetable'

import RoomForm from '../components/RoomForm'
import {
  roomFormPayload,
  type RoomFormPayload
} from '../constants/room-payload'
import useAddRoom from '../mutations/useAddRoom'

const routeApi = getRouteApi('/_auth/rooms/add')

const defaultValues: DefaultValues<RoomFormPayload> = {
  name: '',
  capacity: null,
  type: undefined,
  specialEquipment: '',
  preferredCourses: [],
  preferredUsers: [],
  preferredClasses: []
}

const getDefaultAvailability = (workingHours: TimetableSettings) => {
  const defaultAvailability = {
    time: {
      from: workingHours.startTime,
      to: workingHours.endTime
    },
    isOptional: false
  }

  return daysOfWeek.reduce(
    (acc, day) => ({ ...acc, [day]: [defaultAvailability] }),
    {} as Record<DayOfWeek, (typeof defaultAvailability)[]>
  )
}

const RoomAddView = () => {
  const navigate = routeApi.useNavigate()
  const { t } = useTranslation(['rooms', 'common'])

  const { data: workingHours, isLoading: isTimetableLoading } =
    useTimetableSetttings()

  const { mutate: addRoom, isPending: isAddRoomPending } = useAddRoom({
    onSuccess: ({ response, variables }) => {
      toast({
        variant: 'success',
        title: t('toast.room-add-success', {
          ROOM: variables.name
        })
      })
      navigate({
        to: '/rooms/$roomId',
        params: {
          roomId: response.id
        }
      })
    },
    onError: ({ variables }) => {
      toast({
        variant: 'error',
        title: t('toast.room-add-error', {
          ROOM: variables.name
        })
      })
    }
  })

  const form = useForm<RoomFormPayload>({
    defaultValues,
    resolver: workingHours
      ? zodResolver(roomFormPayload(workingHours))
      : undefined,
    mode: 'all'
  })

  useEffect(() => {
    if (workingHours)
      form.setValue('availability', getDefaultAvailability(workingHours))
  }, [workingHours, form])

  const isLoading = isTimetableLoading || isAddRoomPending

  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('common:navigation.rooms'),
          to: '/rooms'
        },
        {
          title: t('header.add-new-room')
        }
      ]}
      header={t('header.add-new-room')}
      moduleName={t('common:header.administration')}
    >
      <Loading spinning={isLoading}>
        <RoomForm form={form} onSubmit={addRoom} workingHours={workingHours} />
      </Loading>
    </BasicLayout>
  )
}

export default RoomAddView
